import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Bot, User, Volume2, VolumeX, Bell } from 'lucide-react';
import { SOCIAL_LINKS } from '@/lib/index';

export function ChatbotPopup() {
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [messages, setMessages] = useState<{ text: string; isBot: boolean }[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    // Show the "Incoming Message" prompt after 2 seconds
    const timer = setTimeout(() => {
      setIsPromptOpen(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const playNotification = () => {
    if (isMuted) return;
    try {
      const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3');
      audio.volume = 0.4;
      audio.play().catch(e => console.log("Audio play blocked", e));
    } catch (err) {
      console.error("Audio error", err);
    }
  };

  const startChat = () => {
    setIsPromptOpen(false);
    setIsOpen(true);
    playNotification();
    
    // First bot message
    setIsTyping(true);
    setTimeout(() => {
      setMessages([
        { 
          text: "Olá! Sou o Nexus AI, assistente do Kamillo Santos. Analisei sua navegação e identifiquei que você busca escala com IA Preditiva. Como posso te ajudar hoje?", 
          isBot: true 
        }
      ]);
      setIsTyping(false);
    }, 1000);
  };

  const handleSend = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!inputValue.trim()) return;

    const userMsg = inputValue;
    setMessages(prev => [...prev, { text: userMsg, isBot: false }]);
    setInputValue('');
    setIsTyping(true);

    setTimeout(() => {
      let botResponse = "Entendi perfeitamente. Nossa tecnologia Nexus Maximus é ideal para esse cenário. Gostaria de agendar uma consultoria estratégica diretamente com o Kamillo pelo WhatsApp para detalharmos seu projeto?";
      
      if (userMsg.toLowerCase().includes('preço') || userMsg.toLowerCase().includes('valor')) {
        botResponse = "Trabalhamos com projetos personalizados. Para uma proposta precisa, o melhor caminho é uma breve conversa no WhatsApp.";
      } else if (userMsg.toLowerCase().includes('sim') || userMsg.toLowerCase().includes('quero')) {
        botResponse = "Excelente! Clique no botão abaixo para iniciar a conversa no WhatsApp oficial do Kamillo.";
      }

      setMessages(prev => [...prev, { text: botResponse, isBot: true }]);
      setIsTyping(false);
      playNotification();
    }, 1500);
  };

  return (
    <>
      {/* INITIAL PROMPT OVERLAY (Blocking) */}
      <AnimatePresence>
        {isPromptOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-background/90 backdrop-blur-md flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-card border border-primary/30 rounded-3xl p-8 max-w-sm w-full text-center shadow-[0_0_50px_-12px_var(--primary)]"
            >
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                <Bot className="text-primary w-10 h-10" />
                <motion.div 
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className="absolute inset-0 bg-primary rounded-full"
                />
              </div>
              <h3 className="text-xl font-bold mb-2">Mensagem do Nexus AI</h3>
              <p className="text-muted-foreground text-sm mb-8">
                O assistente inteligente do Kamillo Santos tem uma proposta de ROI para você.
              </p>
              <button
                onClick={startChat}
                className="w-full bg-primary text-primary-foreground py-4 rounded-2xl font-bold text-lg hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-3 shadow-xl"
                style={{ boxShadow: '0 8px 24px color-mix(in srgb, var(--primary) 40%, transparent)' }}
              >
                <Bell className="w-5 h-5 animate-bounce" />
                Atender Chamada
              </button>
              <button 
                onClick={() => setIsPromptOpen(false)}
                className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Agora não
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CHAT WINDOW */}
      <div className="fixed bottom-6 right-6 z-[110] flex flex-col items-end">
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="mb-4 w-[calc(100vw-3rem)] sm:w-[380px] h-[550px] bg-card border border-border rounded-3xl shadow-2xl overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="bg-primary p-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center relative">
                    <Bot className="text-white w-6 h-6" />
                    <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm">Nexus AI</h4>
                    <p className="text-white/70 text-xs">Atendimento Especializado</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button 
                    onClick={() => setIsMuted(!isMuted)}
                    className="p-1.5 text-white/80 hover:text-white transition-colors"
                  >
                    {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                  </button>
                  <button 
                    onClick={() => setIsOpen(false)}
                    className="p-1.5 text-white/80 hover:text-white transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide bg-muted/10">
                {messages.map((msg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: msg.isBot ? -10 : 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`flex ${msg.isBot ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`max-w-[85%] p-3.5 rounded-2xl text-sm leading-relaxed ${
                      msg.isBot 
                        ? 'bg-card border border-border text-foreground rounded-tl-none shadow-sm' 
                        : 'bg-primary text-primary-foreground rounded-tr-none shadow-md'
                    }`}>
                      {msg.text}
                    </div>
                  </motion.div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-card border border-border p-3.5 rounded-2xl rounded-tl-none flex gap-1 shadow-sm">
                      <div className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce" />
                      <div className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce [animation-delay:0.2s]" />
                      <div className="w-1.5 h-1.5 bg-primary/40 rounded-full animate-bounce [animation-delay:0.4s]" />
                    </div>
                  </div>
                )}
                
                {messages.length > 1 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pt-2">
                    <a 
                      href={SOCIAL_LINKS.WHATSAPP}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 bg-green-600 text-white p-4 rounded-2xl text-sm font-bold hover:bg-green-700 transition-colors shadow-lg"
                    >
                      Falar com Kamillo no WhatsApp
                    </a>
                  </motion.div>
                )}
              </div>

              {/* Footer Input */}
              <form onSubmit={handleSend} className="p-4 border-t border-border bg-card">
                <div className="relative">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Como posso te ajudar?"
                    className="w-full bg-muted border border-border rounded-2xl pl-4 pr-12 py-3 text-sm focus:outline-none focus:border-primary transition-colors"
                  />
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-primary hover:bg-primary/10 rounded-xl transition-colors"
                  >
                    <Send size={20} />
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Floating Toggle Button */}
        {!isPromptOpen && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(!isOpen)}
            className="w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-2xl flex items-center justify-center relative group"
          >
            <div className="absolute inset-0 rounded-full bg-primary animate-ping opacity-20 group-hover:hidden" />
            {isOpen ? <X size={24} /> : <MessageSquare size={24} />}
          </motion.button>
        )}
      </div>
    </>
  );
}
